import { useEffect, useRef } from "react";
import { Minus, Plus } from "lucide-react";
import { useLang } from "@/i18n";
import { ZAVIA_CONFIG } from "@/services/zaviaRates";
import { usePopoverFlip } from "@/components/booking/usePopoverFlip";

export default function GuestsPanel({ adults, children, maxAdults = 6, maxChildren = 4, onChange, onClose, className = "" }) {
    const { lang, t } = useLang();
    const ref = useRef(null);
    const flip = usePopoverFlip(ref);

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        const onDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onClose();
        };
        document.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onDown);
        document.addEventListener("touchstart", onDown);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("touchstart", onDown);
        };
    }, [onClose]);

    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

    const Row = ({ label, value, min, max, field, testId }) => (
        <div className="flex items-center justify-between py-2">
            <span className="text-sm font-medium text-ink">{label}</span>
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={() => onChange(field === "adults" ? clamp(value - 1, min, max) : adults, field === "children" ? clamp(value - 1, min, max) : children)}
                    disabled={value <= min}
                    aria-label={`${label} −`}
                    data-testid={`${testId}-minus`}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-sand-border text-olive transition-colors hover:bg-sand disabled:opacity-30"
                >
                    <Minus className="h-4 w-4" />
                </button>
                <span className="w-6 text-center text-sm font-semibold text-ink" aria-live="polite" data-testid={`${testId}-value`}>
                    {value}
                </span>
                <button
                    type="button"
                    onClick={() => onChange(field === "adults" ? clamp(value + 1, min, max) : adults, field === "children" ? clamp(value + 1, min, max) : children)}
                    disabled={value >= max}
                    aria-label={`${label} +`}
                    data-testid={`${testId}-plus`}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-sand-border text-olive transition-colors hover:bg-sand disabled:opacity-30"
                >
                    <Plus className="h-4 w-4" />
                </button>
            </div>
        </div>
    );

    return (
        <div
            ref={ref}
            role="dialog"
            aria-label={lang === "en" ? "Guests" : "Huéspedes"}
            data-testid="guests-panel"
            className={`absolute left-0 z-[95] w-full min-w-[240px] rounded-2xl border border-sand-border bg-cream p-4 shadow-[0_24px_60px_rgba(45,51,25,0.25)] sm:w-72 ${flip ? "bottom-full mb-2" : "top-full mt-2"} ${className}`}
        >
            <Row label={t.hero.adults} value={adults} min={1} max={maxAdults} field="adults" testId="guests-adults" />
            <div className="border-t border-sand-border" />
            <Row label={t.hero.children} value={children} min={0} max={maxChildren} field="children" testId="guests-children" />
            {/* Edades de niños: se activan con ZAVIA_CONFIG.childAgesEnabled cuando Zavia/hotel confirmen las reglas */}
            {ZAVIA_CONFIG.childAgesEnabled && children > 0 && (
                <div className="border-t border-sand-border pt-2" data-testid="children-ages">
                    {/* rango configurable: ZAVIA_CONFIG.childAgeRange */}
                </div>
            )}
        </div>
    );
}
